import {
  AccessTokenResponseDto,
  AuthApiAuthControllerForgotPasswordRequest,
  AuthApiAuthControllerLoginRequest,
  AuthApiAuthControllerResetPasswordRequest,
  AuthApiAuthControllerSignupRequest,
  ImpersonatorAccount,
  ValidationError,
} from '@givz/core-client';

import { ClientResponse } from '@givz/core-client/dist/client/utils';
import { useNotificationChannel } from '@hooks/notification';
import { accountKeys } from '@queries/account';
import {
  useForgotPassword,
  useLogin,
  useResetPassword,
  useSignUp,
} from '@queries/auth';
import { useOnCoreClientErrors } from '@queries/client';
import { routes } from '@routes';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isAccessTokenValid, isImpersonator } from '@utils/auth';
import { BrowserStorage } from '@utils/browser';
import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

type AuthContextProps = {
  accessToken?: string;
  errors: ValidationError[] | null;
  forgotPassword: UseMutationResult<
    ClientResponse<void>,
    unknown,
    AuthApiAuthControllerForgotPasswordRequest,
    unknown
  >;
  impersonate: (params: {
    account: ImpersonatorAccount;
    accessToken: string;
  }) => void;
  impersonatorAccessToken?: string;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  isImpersonator: boolean;
  impersonatedAccount?: ImpersonatorAccount;
  login: UseMutationResult<
    ClientResponse<AccessTokenResponseDto>,
    unknown,
    AuthApiAuthControllerLoginRequest,
    unknown
  >;
  logout: () => void;
  resetPassword: UseMutationResult<
    ClientResponse<void>,
    unknown,
    AuthApiAuthControllerResetPasswordRequest,
    unknown
  >;
  signUp: UseMutationResult<
    ClientResponse<AccessTokenResponseDto>,
    unknown,
    AuthApiAuthControllerSignupRequest,
    unknown
  >;
  stopImpersonate: () => void;
};

export const AuthContext = createContext<AuthContextProps | null>(null);
const authKey = '_givz';

function loadAccessToken() {
  try {
    const token = BrowserStorage.get(authKey);
    if (token && isAccessTokenValid(token)) {
      return token;
    }
  } catch (error) {
    // NOTE: Capture sentry
  }
}

export const AuthProvider: FunctionComponent<PropsWithChildren> = (props) => {
  const { children } = props;
  const queryClient = useQueryClient();
  const { success } = useNotificationChannel();
  const navigate = useNavigate();
  const { onApiResponseError } = useOnCoreClientErrors();
  const [errors, setErrors] = useState<ValidationError[] | null>(null);
  const [accessToken, setAccessToken] = useState<string | undefined>(
    loadAccessToken,
  );
  const [impersonatorAccessToken, setImpersonatorAccessToken] = useState<
    string | undefined
  >();

  const [impersonatedAccount, setImpersonatedAccount] = useState<
    ImpersonatorAccount | undefined
  >();

  const impersonate = (params: {
    account: ImpersonatorAccount;
    accessToken: string;
  }) => {
    queryClient.invalidateQueries(accountKeys.base);
    const { accessToken, account } = params;
    setImpersonatedAccount(account);
    setImpersonatorAccessToken(accessToken);
    navigate(routes.dashboard, { replace: true });
  };

  const login = useLogin({
    onError,
    onSuccess: onAuthenticated,
  });

  const signUp = useSignUp({
    onError,
    onSuccess: onAuthenticated,
  });

  const forgotPassword = useForgotPassword({
    onError,
    onSuccess: onForgotPassword,
  });

  const resetPassword = useResetPassword({
    onError,
    onSuccess: onResetPassword,
  });

  useEffect(() => {
    const token = loadAccessToken();
    setAccessToken(token);
  }, [props]);

  const updateAccessToken = (accessToken: string) => {
    BrowserStorage.set(authKey, accessToken);
    setAccessToken(accessToken);
  };

  const removeAccessToken = () => {
    BrowserStorage.remove(authKey);
    setAccessToken(undefined);
  };

  function onError(errors: ValidationError[]) {
    setErrors(errors);
    onApiResponseError(errors);
  }

  function onAuthenticated(params: AccessTokenResponseDto) {
    const { accessToken } = params;
    updateAccessToken(accessToken);
    navigate(
      isImpersonator(accessToken) ? routes.impersonator : routes.dashboard,
      { replace: true },
    );
  }

  function onForgotPassword() {
    success('Please check your email to continue.');
  }

  function onResetPassword() {
    success('Your password have been successfully changed.');
    navigate(routes.login, { replace: true });
  }

  const logout = () => {
    removeAccessToken();
    stopImpersonate();
    queryClient.clear();
    navigate(routes.login, { replace: true });
  };

  const stopImpersonate = () => {
    setImpersonatorAccessToken(undefined);
    setImpersonatedAccount(undefined);
    navigate(routes.impersonator, { replace: true });
    queryClient.invalidateQueries(accountKeys.base);
  };

  const context: AuthContextProps = {
    accessToken,
    errors,
    forgotPassword,
    impersonate,
    impersonatedAccount,
    impersonatorAccessToken,
    isAuthenticated: Boolean(accessToken),
    isImpersonating: Boolean(impersonatorAccessToken),
    isImpersonator: Boolean(accessToken && isImpersonator(accessToken)),
    login,
    logout,
    resetPassword,
    signUp,
    stopImpersonate,
  };
  return (
    <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext) as AuthContextProps;
